import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Typography, Tag, Button, Space, message } from 'antd';
import * as Icon from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { privacyPolicyActionStart, privacyPolicyActionError, getPrivacyPolicyAction, expirePrivacyPolicyAction, activatePrivacyPolicyAction } from '../../core/data/redux/modules/privacyPolicy/actions';
import { getPrivacyPolicyById, expirePrivacyPolicy, activatePrivacyPolicy } from '../../services/adminServices/privacyPolicy';
import { parseContentForEditing } from '../../utils/richText';

const { Title, Text } = Typography;

const PrivacyPolicyDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { privacyPolicies, loading } = useSelector((state: any) => state.privacyPolicy || { privacyPolicies: [], loading: false });
  const [localPolicy, setLocalPolicy] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [policyHtml, setPolicyHtml] = useState<string>('');

  useEffect(() => {
    fetchPrivacyPolicy();
    // eslint-disable-next-line
  }, [id]);

  const fetchPrivacyPolicy = async () => {
    dispatch(privacyPolicyActionStart());
    try {
      const res = await getPrivacyPolicyById(id!);
      if (res.error) throw new Error(res.error);
      dispatch(getPrivacyPolicyAction(res.data));
      setLocalPolicy(res.data);
      
      // Parse rich text content for display
      if (res.data) {
        const { parsedContent } = parseContentForEditing(res.data.privacy_policy_text || '');
        setPolicyHtml(parsedContent);
      }
    } catch (err: any) {
      dispatch(privacyPolicyActionError(err.message));
      message.error(err.message);
    }
  };

  const handleExpire = async () => {
    setActionLoading(true);
    try {
      await expirePrivacyPolicy(id!);
      dispatch(expirePrivacyPolicyAction(id!));
      message.success('Privacy policy expired');
      fetchPrivacyPolicy();
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivate = async () => {
    setActionLoading(true);
    try {
      await activatePrivacyPolicy(id!);
      dispatch(activatePrivacyPolicyAction(id!));
      message.success('Privacy policy activated');
      fetchPrivacyPolicy();
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading || !localPolicy) return <Card loading style={{ margin: 24 }} />;

  return (
    <div className="page-wrapper page-settings" style={{ paddingTop: 0 }}>
      <style>
        {`
          .privacy-content h1 { font-size: 2em; font-weight: bold; margin: 16px 0 8px 0; }
          .privacy-content h2 { font-size: 1.5em; font-weight: bold; margin: 14px 0 6px 0; }
          .privacy-content h3 { font-size: 1.25em; font-weight: bold; margin: 12px 0 4px 0; }
          .privacy-content h4 { font-size: 1.1em; font-weight: bold; margin: 10px 0 4px 0; }
          .privacy-content h5 { font-size: 1em; font-weight: bold; margin: 8px 0 4px 0; }
          .privacy-content h6 { font-size: 0.9em; font-weight: bold; margin: 6px 0 4px 0; }
          .privacy-content p { margin: 8px 0; }
          .privacy-content strong { font-weight: bold; }
          .privacy-content em { font-style: italic; }
          .privacy-content u { text-decoration: underline; }
          .privacy-content s { text-decoration: line-through; }
        `}
      </style>
      <div className="content">
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
            <Title level={4} style={{ margin: 0 }}>Privacy Policy</Title>
            <div style={{ textAlign: 'right' }}>
              <div style={{ marginBottom: 8 }}>
                <Tag color={localPolicy.is_expired ? 'red' : localPolicy.is_active ? 'green' : 'orange'}>
                  {localPolicy.is_expired ? 'expired' : localPolicy.is_active ? 'active' : 'inactive'}
                </Tag>
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                <div>Created: {new Date(localPolicy.created_at).toLocaleDateString()}</div>
                <div>Updated: {new Date(localPolicy.updated_at).toLocaleDateString()}</div>
              </div>
            </div>
          </div>
          
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>Basic Information</Title>
            <div>
              <div style={{ marginBottom: 8 }}>
                <Text strong>Version:</Text> {localPolicy.version}
              </div>
            </div>
          </div>
          
          <div>
            <Title level={5}>Privacy Policy</Title>
            <div 
              className="privacy-content"
              style={{ 
                border: '1px solid #d9d9d9', 
                borderRadius: '6px', 
                padding: '16px', 
                backgroundColor: '#fafafa',
                minHeight: '200px'
              }}
              dangerouslySetInnerHTML={{ __html: policyHtml }}
            />
          </div>
          <Space style={{ marginTop: 32 }}>
            <Link to="/admin/privacy-policy/list-privacy-policies">
              <Button>Back to List</Button>
            </Link>
            {!localPolicy.is_expired && (
              <Button icon={<Icon.StopCircle size={16} />} loading={actionLoading} onClick={handleExpire}>
                Expire
              </Button>
            )}
            {!localPolicy.is_active && (
              <Button icon={<Icon.CheckCircle size={16} />} loading={actionLoading} onClick={handleActivate}>
                Activate
              </Button>
            )}
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicyDetail;

